import React, { useState } from 'react';
import { AVAILABLE_VARS, buildToken } from '../lib/personalize';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Braces, ChevronDown } from 'lucide-react';

/**
 * Insert-variable menu. When a variable is picked, opens an inline
 * fallback input; clicking "Insert" injects {{var|fallback}} (or {{var}}).
 */
export function VarInsertMenu({ onInsert, size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [fb, setFb] = useState('');

  const pick = (v) => {
    setPicked(v);
    setFb(v.suggestedFallback || '');
  };

  const submit = () => {
    if (!picked) return;
    onInsert(buildToken(picked.key, fb));
    setOpen(false);
    setPicked(null);
    setFb('');
  };

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPicked(null); setFb(''); } }}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={`inline-flex items-center gap-1 ${size==='sm'?'text-xs px-2 py-1':'text-sm px-3 py-1.5'} text-zinc-300 hover:text-[#f5d67a] rounded-md border border-zinc-800 hover:border-[#3a2f18] bg-[#0a0a0b]`}>
          <Braces size={12}/>Insert variable <ChevronDown size={12}/>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#111110] border-zinc-800 text-zinc-100 w-80 p-2">
        {!picked ? (
          <div>
            <div className="text-xs text-zinc-500 px-2 py-1">Pick a variable</div>
            {AVAILABLE_VARS.map(v => (
              <button key={v.key} type="button" onClick={()=>pick(v)} className="w-full text-left px-2 py-2 rounded hover:bg-zinc-900 flex items-center justify-between">
                <div>
                  <div className="text-sm text-zinc-100">{v.label}</div>
                  <div className="text-xs text-zinc-500 font-mono">{`{{${v.key}}}`}</div>
                </div>
                {v.suggestedFallback && <span className="text-[10px] text-zinc-500">fallback: "{v.suggestedFallback}"</span>}
              </button>
            ))}
          </div>
        ) : (
          <div className="p-1">
            <div className="text-sm text-zinc-100">{picked.label}</div>
            <div className="text-xs text-zinc-500 font-mono">{`{{${picked.key}}}`}</div>
            <div className="mt-3">
              <label className="text-xs text-zinc-400">Fallback text if missing (optional)</label>
              <Input
                autoFocus
                value={fb}
                onChange={(e)=>setFb(e.target.value)}
                placeholder={picked.suggestedFallback || 'e.g. your company'}
                className="mt-1 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] h-8"
                onKeyDown={(e)=>{ if (e.key==='Enter') { e.preventDefault(); submit(); } }}
              />
              <div className="mt-1 text-[10px] text-zinc-600">Preview: <span className="font-mono text-zinc-400">{buildToken(picked.key, fb)}</span></div>
            </div>
            <div className="mt-3 flex justify-between gap-2">
              <Button variant="outline" size="sm" onClick={()=>setPicked(null)} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Back</Button>
              <Button size="sm" onClick={submit} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium">Insert</Button>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Chip bar for existing variables in a template. Each chip shows
 * "First name" or "Company → your company" and clicking opens an
 * editor to change/remove the fallback.
 */
export function VarChipBar({ variables, onUpdate, onRemove }) {
  if (!variables || variables.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Variables:</span>
      {variables.map((v, i) => (
        <VarChip key={`${v.name}-${i}`} v={v} onUpdate={onUpdate} onRemove={onRemove}/>
      ))}
    </div>
  );
}

function VarChip({ v, onUpdate, onRemove }) {
  const [fb, setFb] = useState(v.fallback || '');
  const [open, setOpen] = useState(false);
  const labelMap = { first_name:'First name', last_name:'Last name', company:'Company', job_title:'Job title' };
  const label = labelMap[v.name] || v.name;
  const hasFb = v.fallback && v.fallback.length > 0;

  const save = () => {
    onUpdate?.(v, fb);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o)=>{ setOpen(o); if (o) setFb(v.fallback || ''); }}>
      <PopoverTrigger asChild>
        <button type="button" className={`group inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${hasFb ? 'bg-[#1a1815] border-[#3a2f18] text-[#f5d67a]' : 'bg-zinc-900 border-zinc-800 text-zinc-300'} hover:border-[#d4a94a]`}>
          <span>{label}</span>
          {hasFb && <span className="text-zinc-400 text-[10px] font-mono max-w-[140px] truncate">{'→'} "{v.fallback}"</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="bg-[#111110] border-zinc-800 text-zinc-100 w-72 p-3">
        <div className="text-sm">{label}</div>
        <div className="text-xs text-zinc-500 font-mono">{`{{${v.name}}}`}</div>
        <div className="mt-3">
          <label className="text-xs text-zinc-400">Fallback text if missing</label>
          <Input
            autoFocus
            value={fb}
            onChange={(e)=>setFb(e.target.value)}
            placeholder="Leave empty to require this field"
            className="mt-1 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] h-8"
            onKeyDown={(e)=>{ if (e.key==='Enter') { e.preventDefault(); save(); } }}
          />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <button type="button" onClick={()=>{ onRemove?.(v); setOpen(false); }} className="text-xs text-red-400 hover:text-red-300">Remove variable</button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900 h-7 text-xs" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={save} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium h-7 text-xs">Save</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
